import { z } from 'zod'
import './registry'
import {
  TenantIdSchema,
  UlidSchema,
  IsoDateSchema,
  MoneySchema,
  CurrencyCodeSchema,
  PaginationCursorSchema,
  PaginationLimitSchema,
} from './common'
import { ContentLocaleSchema } from './content-pages'
import { RestockChannelSchema, VatTreatmentSchema } from './transactions'
// records.ts does not import orders.ts, so this is not a cycle.
import { RecordConditionSchema } from './records'

/**
 * Sales channel an order came in through. Channel-agnostic by design (the order
 * hub is shared across every channel): `discogs` orders arrive via the poller,
 * `webshop` orders via POST /shop/checkout (api plan Phase 3). Further
 * channels slot in here without reshaping the order model.
 */
export const OrderChannelSchema = z.enum(['discogs', 'webshop']).openapi('OrderChannel')

/**
 * Normalised, channel-independent order lifecycle. Each channel adapter maps its
 * native status vocabulary onto this set (for Discogs see `lib/discogs-marketplace.ts`):
 *
 *   new              — just received, nothing done yet
 *   awaiting_payment — invoice sent / payment pending (buyer hasn't paid)
 *   paid             — payment received; ready to pick & pack
 *   in_progress      — being prepared for shipment
 *   shipped          — dispatched to the buyer
 *   cancelled        — cancelled (non-paying buyer, item unavailable, buyer request)
 *   refunded         — money returned
 *
 * `new | awaiting_payment | paid | in_progress` are the OPEN (needs-work) shelf;
 * `shipped | collected | cancelled | refunded` are CLOSED. Drives the GSI6 open/closed bucket.
 */
export const OrderStatusSchema = z
  .enum([
    'new',
    'awaiting_payment',
    'paid',
    'in_progress',
    'shipped',
    /** Webshop pickup orders only: handed over at the counter (the pickup twin of `shipped`). */
    'collected',
    'cancelled',
    'refunded',
  ])
  .openapi('OrderStatus')

export type OrderStatus = z.infer<typeof OrderStatusSchema>

/** open = still needs staff work; closed = done/dead. Mirrors records' ShelfState split. */
export const OrderShelfSchema = z.enum(['open', 'closed']).openapi('OrderShelf')
export type OrderShelf = z.infer<typeof OrderShelfSchema>

const OPEN_ORDER_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  'new',
  'awaiting_payment',
  'paid',
  'in_progress',
])

/** Derive the GSI6 open/closed bucket from a normalised order status. */
export function orderShelfFor(status: OrderStatus): OrderShelf {
  return OPEN_ORDER_STATUSES.has(status) ? 'open' : 'closed'
}

/**
 * The four questions the rest of the system asks about an order status, answered
 * ONCE, here, beside the enum they read from.
 *
 * They were previously spelled out wherever they were needed — the transaction
 * mirror, its backfill script, the Mollie webhook, the open-count endpoint, the
 * resume gates and the storefront checkout store each carried their own copy of
 * "is this paid" or "is this closed". Nine spellings of one vocabulary, kept in
 * agreement by comments. Adding a status (`partially_refunded`, `on_hold`) then
 * means finding nine unrelated files, and every miss fails QUIETLY: a closed
 * order resurrected as revenue, a KPI that under-reports, a customer's own holds
 * abandoned — the totals stay plausible either way, which is the exact failure
 * mode the transaction mirror exists to close.
 *
 * Exported through the schemas barrel on purpose: the storefront store and the
 * operational scripts consume these too, and neither can import from `lib/`
 * (those modules build a DynamoDB client at load). Same treatment, and for the
 * same reason, as `schemas/audit-fanout.ts`.
 */
const MONEY_RECEIVED_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  'paid',
  'in_progress',
  'shipped',
  'collected',
])
const CLOSED_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>(['cancelled', 'refunded'])
const FINISHED_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>(['shipped', 'collected'])

/** The money came in. Mirroring a sale, and refusing to mirror an attempt. */
export const isMoneyReceived = (status: OrderStatus): boolean => MONEY_RECEIVED_STATUSES.has(status)

/**
 * Done WITHOUT a sale standing: cancelled or refunded. Distinct from "closed"
 * (`orderShelfFor`), which also covers the delivered ones — a shipped order is
 * off the queue but its money is still ours.
 */
export const isOrderClosed = (status: OrderStatus): boolean => CLOSED_STATUSES.has(status)

/** Delivered to the customer — the shop's work on it is done. */
export const isOrderFinished = (status: OrderStatus): boolean => FINISHED_STATUSES.has(status)

/**
 * Was this already-sold copy sold by THIS order? Shared by the webhook's
 * sale flip (lib/webshop-orders.ts), the refund's "may this copy still be
 * restocked or settled" (handlers/orders.ts) and the staff refund panel, so
 * the three can't drift.
 *
 * The sale link IS the order's own ULID (the mirrored transaction is keyed on
 * it — lib/order-transaction.ts), so the comparison is exact: a copy sold by a
 * DIFFERENT webshop order, or over the till, no longer answers "already sold"
 * and correctly falls through to the paid-but-lost path.
 *
 * A null link is treated as ours, and must stay that way. Copies sold before
 * the sale link was stamped carry null forever, and reading those as "not
 * ours" would make a redelivered webhook for an old order claim the copy was
 * lost — auto-refunding a perfectly good sale. The precision is for new sales;
 * the tolerance is for the ones already on the shelf.
 */
export function soldUnderThisOrder(saleTransactionId: string | null, orderId: string): boolean {
  return saleTransactionId === null || saleTransactionId === orderId
}

/**
 * The `channelStatusRaw` the Mollie webhook's paid-but-lost net stamps on an
 * order it refunded: WHY the money went back. The customer's apology mail is
 * worded by it (lib/webshop-emails.ts), because "the record had just sold" is
 * wrong for an order that was already cancelled when the payment landed (by
 * the customer, staff, the hold sweep or a till takeover). Read from the order
 * rather than carried on the mail job, so either side may deploy first: an
 * older worker simply sends the older wording.
 */
export const PAID_BUT_LOST_REASON = {
  copyGone: 'refunded: copy no longer available',
  paidAfterCancel: 'refunded: paid after cancellation',
} as const

/**
 * Open AND waiting on US. Excludes `awaiting_payment`, which waits on the
 * customer (and which the reservation sweeper cancels on its own), so a badge
 * built on this nags only about work someone can actually do.
 */
export const isOrderActionable = (status: OrderStatus): boolean =>
  OPEN_ORDER_STATUSES.has(status) && status !== 'awaiting_payment'

/**
 * Statuses a staff member may set via POST /orders/{id}/status. Deliberately a
 * SUBSET of OrderStatus: `new`/`awaiting_payment`/`paid`/`refunded` are
 * channel-driven (Discogs sets them automatically and rejects manual changes
 * after payment), so they're never offered as manual actions. The handler
 * further restricts these by the order's current raw channel status — mirroring
 * the records ALLOWED_TRANSITIONS pattern.
 */
export const OrderActionStatusSchema = z
  .enum(['in_progress', 'shipped', 'collected', 'cancelled'])
  .openapi('OrderActionStatus')
export type OrderActionStatus = z.infer<typeof OrderActionStatusSchema>

/**
 * Which manual actions are valid from a given normalised status — Discogs' own
 * state machine, encoded once and shared by the backend (to validate) and the
 * frontend drawer (to show only the buttons Discogs would accept). Mirrors the
 * records ALLOWED_TRANSITIONS pattern:
 *   - before payment: only cancel (Discogs drives the payment states itself)
 *   - paid: prepare, ship or hand over; in progress: ship or hand over;
 *     terminal states: nothing.
 * Discogs stays the final authority — the handler maps any 4xx to a friendly error.
 *
 * This is the STATUS matrix only. `shipped` and `collected` are both listed
 * from paid/in_progress because which one applies depends on the ORDER, not
 * the status — see `orderActionsFor`, which is what callers should use.
 */
export function allowedOrderActions(status: OrderStatus): readonly OrderActionStatus[] {
  switch (status) {
    case 'new':
    case 'awaiting_payment':
      return ['cancelled']
    case 'paid':
      return ['in_progress', 'shipped', 'collected']
    case 'in_progress':
      return ['shipped', 'collected']
    case 'shipped':
    case 'collected':
    case 'cancelled':
    case 'refunded':
      return []
  }
}

/**
 * A webshop order the customer collects at the counter — it never ships.
 * The one statement of the pickup rule, shared by `orderActionsFor` and the
 * drawer's message templates (no "on its way" for a counter hand-over).
 */
export function isWebshopPickup(order: Pick<Order, 'channel' | 'shippingMethod'>): boolean {
  return order.channel === 'webshop' && order.shippingMethod === 'pickup'
}

/**
 * The actions staff may actually be offered for THIS order: the status matrix
 * narrowed by how the order reaches the customer.
 *
 *   - Discogs orders never get `collected` — Discogs has no such state; its
 *     buyer is always shipped to.
 *   - A webshop PICKUP order gets `collected` INSTEAD of `shipped`. That is the
 *     whole point of the status: before it existed, staff had to mark a
 *     counter hand-over as a shipment (api plan Phase 4 build notes).
 *   - A webshop SHIP order gets `shipped`, never `collected`.
 *
 * Shared by the backend (to validate) and the drawer (to render only the
 * buttons that will succeed), like `allowedOrderActions` — one rule, two
 * consumers.
 */
export function orderActionsFor(
  order: Pick<Order, 'status' | 'channel' | 'shippingMethod'>
): readonly OrderActionStatus[] {
  const pickup = isWebshopPickup(order)
  return allowedOrderActions(order.status).filter(a =>
    pickup ? a !== 'shipped' : a !== 'collected'
  )
}

/** Who authored an order message. `system` = a status-change note Discogs prepends. */
export const OrderMessageFromSchema = z
  .enum(['buyer', 'seller', 'system'])
  .openapi('OrderMessageFrom')

export const BuyerSchema = z
  .object({
    /**
     * The channel's display handle for the buyer — the Discogs username, or
     * for webshop orders the customer's email (the workbench renders this
     * field everywhere, so both channels fill it).
     */
    username: z.string(),
    /** Buyer's real name when the channel exposes it (webshop: the checkout form's name), else null. */
    realName: z.string().nullable(),
    /**
     * Buyer email — webshop orders only (Discogs never exposes one). Drives
     * the transactional mail and, in Phase 5, the guest-order→account attach
     * by exact match. Defaulted so pre-existing Discogs orders parse on read.
     */
    email: z.string().nullable().default(null),
  })
  .openapi('Buyer')

/**
 * One line item on an order. `recordId` links back to the catalogue record: first from
 * the record ULID Discogs surfaces in the order item's `private_comments` (the
 * `external_id` we stamp on the listing — the orders endpoint returns `external_id`
 * itself empty), then falling back to `listing_id` → `record.discogsListing.listingId`.
 * null when no local record could be matched (e.g. listed directly on Discogs outside
 * this app). `priceEur` is the item's sale price in the shop's settlement currency.
 */
export const OrderItemSchema = z
  .object({
    recordId: UlidSchema.nullable(),
    listingId: z.string().nullable(),
    releaseId: z.string().nullable(),
    description: z.string(),
    condition: z.string().nullable(),
    priceEur: MoneySchema,
    /**
     * Per-line VAT snapshot (webshop orders; api plan Phase 3A) — the same
     * capture-at-sale pattern as `TransactionLineSchema`: margin-scheme lines
     * must never state a VAT amount (plan §10.2), standard lines carry the
     * rate that was in force. Snapshotted at order creation from the record's
     * immutable `isNew` + the tenant's configured rate, so later config
     * changes never rewrite history. Null on Discogs lines (that channel
     * handles its own tax) and on orders predating the field — union, not
     * `.nullable()`, because `VatTreatment` is a registered component.
     */
    vatTreatment: z.union([VatTreatmentSchema, z.null()]).default(null),
    /** The standard rate charged (0..100) when `vatTreatment === 'standard'`, else null. */
    vatRatePercent: z.number().min(0).max(100).nullable().default(null),
  })
  .openapi('OrderItem')

/** How a webshop order reaches the customer. */
export const ShippingMethodSchema = z.enum(['ship', 'pickup']).openapi('ShippingMethod')

/**
 * Normalised PSP payment lifecycle — the wire mirror of `PaymentStatus` in
 * lib/payments/provider.ts (that module owns the seam; this one owns the
 * persisted contract, and the two must stay in step).
 */
export const PaymentStatusSchema = z
  .enum(['pending', 'paid', 'canceled', 'expired', 'failed', 'refunded'])
  .openapi('PaymentStatus')

/**
 * Address snapshot captured at checkout (webshop plan §4). A SNAPSHOT on
 * purpose: the customer editing their profile later (Phase 5) must never
 * rewrite where an already-placed order was promised to go.
 */
export const ShippingAddressSchema = z
  .object({
    name: z.string(),
    street: z.string(),
    houseNumber: z.string(),
    addition: z.string().nullable(),
    postcode: z.string(),
    city: z.string(),
    /** ISO 3166-1 alpha-2, uppercase ("NL"). */
    country: z.string(),
    /** Optional carrier-contact number. Defaulted so pre-phone orders parse on read. */
    phone: z.string().nullable().default(null),
  })
  .openapi('ShippingAddress')

/** Fulfilment details once a webshop order ships. */
export const OrderFulfilmentSchema = z
  .object({
    trackingNumber: z.string(),
    carrier: z.string().nullable(),
  })
  .openapi('OrderFulfilment')

/**
 * Wire-shape of an order. Like RecordSchema this is non-strict: the persisted DDB
 * item also carries PK/SK/gsi6pk/gsi6sk which zod strips on parse.
 */
export const OrderSchema = z
  .object({
    id: UlidSchema,
    tenantId: TenantIdSchema,
    channel: OrderChannelSchema,
    /** The channel's own order id (Discogs order id). Unique within (tenant, channel). */
    channelOrderId: z.string(),
    /**
     * Human-facing sequential number (`RV-2026-0834`, lib/order-number.ts) —
     * what customers and staff SAY; the ULID stays the technical key.
     * Defaulted null so pre-existing orders and Discogs orders parse on read.
     */
    orderNumber: z.union([z.string(), z.null()]).default(null),
    status: OrderStatusSchema,
    /** Exact channel status string (e.g. "Payment Received") — drives which actions are still allowed. */
    channelStatusRaw: z.string(),
    buyer: BuyerSchema,
    items: z.array(OrderItemSchema),
    totalEur: MoneySchema,
    shippingEur: MoneySchema.nullable(),
    currency: CurrencyCodeSchema,
    messageCount: z.number().int().nonnegative(),
    /** True when the latest message is from the buyer and hasn't been actioned. */
    unreadForSeller: z.boolean(),
    /** Seller-only note, never sent to the buyer. */
    internalNote: z.string().nullable(),
    /**
     * When the seller last opened this order's message thread. `unreadForSeller` is
     * computed against it (a buyer message newer than this counts as unread), so the
     * badge reflects "read", not just "last message is from the buyer". Defaulted so
     * orders written before this field still parse on read.
     */
    lastReadAt: IsoDateSchema.nullable().default(null),
    createdAt: IsoDateSchema,
    lastActivityAt: IsoDateSchema,
    // ── Webshop-channel fields (api plan Phase 3A) ──────────────────
    // All defaulted null so Discogs orders (and webshop orders predating a
    // field) parse on read; unions where the inner schema is registered.
    /** Where a shipped webshop order goes — null for pickup and for Discogs orders. */
    shippingAddress: z.union([ShippingAddressSchema, z.null()]).default(null),
    /** ship | pickup for webshop orders; null on Discogs orders. */
    shippingMethod: z.union([ShippingMethodSchema, z.null()]).default(null),
    /** Tracking details once shipped (webshop); null before that / on Discogs orders. */
    fulfilment: z.union([OrderFulfilmentSchema, z.null()]).default(null),
    /** Locale the customer checked out in — picks the language of every later email. */
    customerLocale: z.union([ContentLocaleSchema, z.null()]).default(null),
    /**
     * The host the checkout request reached the shop API on (`ShopTenantCtx.host`,
     * lib/shop-tenant.ts). Despite the name, on the deployed platform that is
     * the API CDN's own domain, NOT the customer-facing storefront. Support
     * context only: customer links come from `WebshopConfig.storefrontUrl`
     * (lib/webshop-orders.ts `orderMagicLinkUrl`) — never build one from this.
     */
    storefrontHost: z.string().nullable().default(null),
    // ── Payment (api plan Phase 4) ──────────────────────────────────
    /** PSP payment id (Mollie `tr_…`); null before payment creation and on Discogs orders. */
    pspPaymentId: z.string().nullable().default(null),
    /**
     * Normalised PSP status (lib/payments/provider.ts). Distinct from the
     * ORDER status on purpose: `paid` here is the money fact reported by the
     * PSP, while the order's `paid` is our own transition, made only after the
     * webhook verified that fact and the records moved to sold_online.
     */
    pspStatus: z.union([PaymentStatusSchema, z.null()]).default(null),
    /**
     * Refund bookkeeping (plan §10.3 — a withdrawal refund includes the
     * original shipping). Partial refunds accumulate in `refundedTotal`, so a
     * second refund can never exceed what was actually charged.
     */
    pspRefundIds: z.array(z.string()).default([]),
    refundedTotal: z.union([MoneySchema, z.null()]).default(null),
    refundedAt: z.union([IsoDateSchema, z.null()]).default(null),
  })
  .openapi('Order')

export const OrderMessageSchema = z
  .object({
    // A stable list key for the thread. Channel-agnostic: Discogs messages have no
    // id of their own, so this is synthesised (index-based) by the live fetch — not
    // a ULID. Future channels may map a real id here.
    id: z.string(),
    from: OrderMessageFromSchema,
    body: z.string(),
    createdAt: IsoDateSchema,
    /** The channel's own message id, when it has one — used to dedupe on re-poll. Null for locally-authored. */
    channelMessageId: z.string().nullable(),
  })
  .openapi('OrderMessage')

// ── Requests / responses ──────────────────────────────────────────────

export const ListOrdersQuerySchema = z
  .object({
    /** open (needs work, default) vs closed (archive). */
    shelf: OrderShelfSchema.optional(),
    status: OrderStatusSchema.optional(),
    limit: PaginationLimitSchema,
    cursor: PaginationCursorSchema,
  })
  .openapi('ListOrdersQuery')

export const ListOrdersResponseSchema = z
  .object({
    items: z.array(OrderSchema),
    nextCursor: z.string().optional(),
  })
  .openapi('ListOrdersResponse')

/**
 * GET /orders/open-count — the nav badge + dashboard KPI. `open` is the whole
 * GSI6 open bucket (the workbench queue); `actionable` excludes
 * `awaiting_payment`, where staff can do nothing — the customer either pays
 * or the ~30-min hold sweeper cancels the order.
 */
export const OpenOrdersCountResponseSchema = z
  .object({
    /** Orders on the OPEN shelf: new + awaiting_payment + paid + in_progress. */
    open: z.number().int().min(0),
    /**
     * The open orders staff can actually act on — `open` minus
     * `awaiting_payment`, which is waiting on the CUSTOMER (and which the
     * reservation sweeper cancels on its own). Drives the nav badge.
     */
    actionable: z.number().int().min(0),
    /**
     * Orders finished this calendar month: `shipped` or `collected`, by last
     * activity. Cancelled and refunded orders are closed but NOT finished, so
     * they are excluded. Shown as the delta line under the open-orders tile.
     *
     * Nullable with a null default for DEPLOY SKEW: the frontend `.parse()`s
     * this response, so an API that predates the field would otherwise throw
     * and blank the whole tile — open count, badge and all — over a missing
     * delta line. Null renders as "no delta yet", which is already a state the
     * strip handles; a `0` default would instead state a number that is wrong.
     */
    completedThisMonth: z.number().int().min(0).nullable().default(null),
  })
  .openapi('OpenOrdersCountResponse')

/**
 * Staff order search. ONE box, two accepted forms, because the counter phone
 * gives staff whichever the customer happens to read out: the human order
 * number from their confirmation mail (`RV-2026-0834`) or the technical id
 * from a link or an audit row (`01M1HRMKDR2Y31Z1NY9SWV9BE1`, with or without
 * a leading `#`). Which one it is, is decided by shape at the handler, not by
 * a mode the user has to pick.
 */
export const OrderLookupQuerySchema = z
  .object({ q: z.string().trim().min(1).max(64) })
  .openapi('OrderLookupQuery')
export type OrderLookupQuery = z.infer<typeof OrderLookupQuerySchema>

export const OrderMessagesResponseSchema = z
  .object({ items: z.array(OrderMessageSchema) })
  .openapi('OrderMessagesResponse')

/**
 * Send a message to the buyer, optionally changing the order status in the same
 * action (Discogs supports a combined message+status post). At least one of the
 * two must be meaningful — the handler rejects an empty body with no status.
 */
export const AddOrderMessageRequestSchema = z
  .object({
    body: z.string().trim().max(2000).default(''),
    status: OrderActionStatusSchema.optional(),
    /**
     * Idempotency key (one ULID per logical send, reused across retries). The
     * send POSTs to Discogs — a non-idempotent external write — so a double-submit
     * or client retry on a slow response would otherwise post the buyer two
     * messages. The handler replays the stored response for a repeated requestId.
     */
    requestId: UlidSchema,
  })
  .strict()
  .openapi('AddOrderMessageRequest')

export const UpdateOrderStatusRequestSchema = z
  .object({
    status: OrderActionStatusSchema,
    /** Idempotency key — see AddOrderMessageRequestSchema. The status change POSTs to Discogs. */
    requestId: UlidSchema,
    /**
     * Tracking details, accepted ONLY for a webshop-channel `shipped` action
     * (the handler 400s it elsewhere — a strict boundary, not a silent drop).
     * Extends this request rather than adding a parallel /ship route: the
     * api-stack rides near CloudFormation's resource ceiling (api plan §3C).
     */
    tracking: z
      .object({
        number: z.string().trim().min(1).max(60),
        carrier: z.string().trim().min(1).max(60).optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .openapi('UpdateOrderStatusRequest')

/**
 * Which order line a refund is about.
 *
 * An OrderItem has no id of its own, so identity is composed: the Discogs
 * listing id when there is one (Discogs' own refund dialog keys its rows on
 * exactly that — the "Item ID" column), else our record id, else the line's
 * index. Stored on the refund alongside `recordId` so the line can be found
 * again even after the Discogs poller rebuilds `items` wholesale.
 */
/**
 * Stable identity for an order line.
 *
 * `OrderItem` has no id. The Discogs listing id is the natural key where there
 * is one — Discogs' own refund dialog keys its rows on exactly that ("Item ID")
 * — then our record id, then the position. Position is the last resort because
 * it is the only one the poller's wholesale rebuild could change.
 */
export function orderLineKey(item: OrderItem, index: number): string {
  return item.listingId ?? item.recordId ?? String(index)
}

export const OrderRefundLineSchema = z
  .object({
    lineKey: z.string().min(1).max(128),
    /** The copy refunded, or null for a line we never linked to our catalogue. */
    recordId: UlidSchema.nullable().default(null),
    amountEur: MoneySchema,
    /** Whether this copy went back into sellable stock. */
    restocked: z.boolean().default(false),
    /**
     * Whether the supplier's share of THIS amount was clawed back although the
     * copy stayed out of stock (staff ticked "verrekenen met leverancier"). A
     * later restock of the same line then claws back only the rest of the
     * line, never the whole of it a second time. False for older rows, which
     * is true: a refund without restock clawed nothing back before this.
     */
    settledWithSupplier: z.boolean().default(false),
  })
  .strict()
  .openapi('OrderRefundLine')
export type OrderRefundLine = z.infer<typeof OrderRefundLineSchema>

/**
 * One refund EVENT on an order — append-only, never updated.
 *
 * The order row cannot hold this. The Discogs poller rebuilds an order from the
 * raw Discogs payload every poll and Puts the whole row, keeping only a handful
 * of our own fields, so refund state written there survives minutes at best.
 * These live as ORDERREFUND# child items instead (lib/ddb.ts), which the poller
 * never touches, and order-level totals are DERIVED by summing them — which is
 * also what makes two concurrent refunds safe, where the previous
 * read-modify-write of `refundedTotal` silently lost one.
 */
export const OrderRefundSchema = z
  .object({
    id: UlidSchema,
    tenantId: TenantIdSchema,
    channel: OrderChannelSchema,
    channelOrderId: z.string(),
    /** Per-line amounts. Empty only for a shipping-only refund. */
    lines: z.array(OrderRefundLineSchema).max(50).default([]),
    /** Refunded shipping, which belongs to no copy. */
    shippingEur: z.union([MoneySchema, z.null()]).default(null),
    /** lines + shipping. Stored, not derived, so a later rounding change cannot restate history. */
    totalEur: MoneySchema,
    /**
     * How the money actually moved. `online` = our Mollie refund (id in
     * `pspRefundId`); `external` = a Discogs order, refunded by a human in
     * Discogs' UI because their API has no refund endpoint.
     */
    method: z.enum(['online', 'external']),
    pspRefundId: z.union([z.string(), z.null()]).default(null),
    reason: z.string().max(300),
    /** Cognito sub of the admin who issued it. */
    actor: z.string(),
    createdAt: IsoDateSchema,
  })
  // NOT `.strict()`, like every other schema that round-trips through DynamoDB:
  // the stored item carries PK and SK alongside these fields, so a strict schema
  // could never parse the row it had just written — and `listOrderRefunds` turns
  // an unparseable row into a thrown error, which would have made the SECOND
  // refund on any order a 500.
  .openapi('OrderRefund')
export type OrderRefund = z.infer<typeof OrderRefundSchema>

/**
 * POST /orders/{id}/refund — staff-initiated refund, full or per line.
 *
 * `mode: 'full'` refunds everything still refundable (for a withdrawal that
 * INCLUDES the original shipping, plan §10.3), closes the order and restocks
 * every copy. `mode: 'partial'` refunds only the lines named, each capped at
 * that line's price, plus optionally some shipping.
 *
 * A line ABSENT from `lines` is not refunded. There is deliberately no way to
 * say "refund 0.00" — an empty amount in the UI means "leave this line alone",
 * and the two must not collapse into the same request.
 */
/**
 * What the refund panel needs to render, and what it may still hand back.
 *
 * Derived server-side on purpose. The panel caps every input at what is left,
 * and those caps ARE the server's caps — computed by the same `refundableState`
 * the handler enforces with, so the two can never drift into a UI that offers a
 * refund the API then refuses.
 *
 * `lineKey` is returned rather than re-derived in the browser. Its rule
 * (`listingId ?? recordId ?? index`) lives in a backend lib the frontend cannot
 * import, so a UI that guessed would key Discogs lines on the record id while
 * the server keys them on the listing id — and every Discogs refund would come
 * back ERR_REFUND_LINE_NOT_FOUND. Returning the key also survives the poller
 * rebuilding `items` between the two fetches.
 */
export const OrderRefundLineStateSchema = z
  .object({
    lineKey: z.string(),
    recordId: z.union([UlidSchema, z.null()]).default(null),
    listingId: z.union([z.string(), z.null()]).default(null),
    description: z.string(),
    /** What the buyer paid for this line. */
    priceEur: MoneySchema,
    refundedEur: MoneySchema,
    /** The cap on a further refund of this line. */
    remainingEur: MoneySchema,
  })
  .openapi('OrderRefundLineState')

export const RefundOrderRequestSchema = z
  .object({
    mode: z.enum(['full', 'partial']),
    /**
     * Partial: what to refund per line. Full: every remainder is refunded
     * regardless, and a line entry only carries that copy's restock choice
     * (its amount is ignored; a line left out is restocked).
     */
    lines: z
      .array(
        z
          .object({
            lineKey: z.string().min(1).max(128),
            amountEur: MoneySchema,
            /** Put the copy back into sellable stock (and re-list it if online). */
            restock: z.boolean().default(true),
            restockChannel: RestockChannelSchema.optional(),
            /** Correct the grade on the way back — usually WHY it was refunded. */
            newCondition: RecordConditionSchema.optional(),
            newPriceEur: MoneySchema.optional(),
            /**
             * Only for a copy that does NOT go back into stock: whether the
             * supplier's payout shares in this refund (their share of the
             * refunded amount is taken back). Absent means NO: the shop
             * carries a lost parcel and its own grading mistakes, not the
             * supplier (the shop's rule, 2026-10-07); true is the exception.
             * A restocked copy always settles in full: its sale is undone.
             * Optional, NOT defaulted: a default would be written into every
             * parsed body, including a pending refund the staff app resends,
             * and an API older than this field rejects the unknown key.
             */
            settleWithSupplier: z.boolean().optional(),
            /**
             * Only for a copy that does NOT go back into stock: it was lost in
             * the post, so the Return row records `lost_in_transit` instead of
             * `defect`. Sent only when true (absent means no on every API
             * version). A lost copy can't go back into stock: rejected with
             * `restock: true`.
             */
            lostInTransit: z.boolean().optional(),
          })
          .strict()
      )
      .max(50)
      .default([]),
    shippingEur: MoneySchema.optional(),
    // 255, not the stored row's 300: the reason is also the Mollie refund
    // description, which Mollie rejects above 255. Capping it where staff type it
    // keeps what they wrote and what the customer's bank shows identical.
    reason: z.string().trim().max(255).optional(),
    /** Idempotency key — a PSP refund is a non-idempotent money movement. */
    requestId: UlidSchema,
  })
  .strict()
  .superRefine((v, ctx) => {
    for (const [i, line] of v.lines.entries()) {
      if (line.lostInTransit && line.restock) {
        ctx.addIssue({
          code: 'custom',
          message: 'a copy lost in the post cannot go back into stock',
          path: ['lines', i, 'restock'],
        })
      }
    }
    if (v.mode === 'partial' && v.lines.length === 0 && v.shippingEur === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a partial refund must name at least one line or some shipping',
        path: ['lines'],
      })
    }
    // One entry per line, or the per-line cap stops capping: two €25 entries
    // against one €25 copy each pass the check on their own and together refund
    // it twice. The order-wide cap would only catch that on a small order.
    const seen = new Set<string>()
    for (const [i, line] of v.lines.entries()) {
      if (seen.has(line.lineKey)) {
        ctx.addIssue({
          code: 'custom',
          message: 'each line may appear only once in a refund',
          path: ['lines', i, 'lineKey'],
        })
      }
      seen.add(line.lineKey)
    }
  })
  .openapi('RefundOrderRequest')

/**
 * A refund that was started and not yet recorded: the claim the refund
 * endpoint writes before it moves any money (lib/order-refund.ts). Carries the
 * ORIGINAL request, because that is the refund the PSP may already have paid
 * out — finishing it means sending exactly this body again.
 */
export const PendingRefundSchema = z
  .object({
    requestId: UlidSchema,
    channel: OrderChannelSchema,
    channelOrderId: z.string(),
    body: RefundOrderRequestSchema,
    startedAt: IsoDateSchema,
    /** How many times this refund has been attempted, the first included. */
    attempts: z.number().int().min(1),
  })
  .openapi('PendingRefund')

export const OrderRefundStateSchema = z
  .object({
    lines: z.array(OrderRefundLineStateSchema),
    shippingPaidEur: MoneySchema,
    shippingRefundedEur: MoneySchema,
    shippingRemainingEur: MoneySchema,
    refundedTotalEur: MoneySchema,
    /** The order-wide cap — never more than this, however it is split. */
    remainingEur: MoneySchema,
    fullyRefunded: z.boolean(),
    /** Every refund recorded so far, oldest first. */
    refunds: z.array(OrderRefundSchema),
    /**
     * A refund that started but was never recorded — the panel offers to
     * finish it, and refuses to start another until it is. Defaulted so a
     * newer frontend reads an older API as "nothing pending".
     */
    pending: z.union([PendingRefundSchema, z.null()]).default(null),
  })
  .openapi('OrderRefundState')

export const SetOrderNoteRequestSchema = z
  .object({ note: z.string().max(2000).nullable() })
  .strict()
  .openapi('SetOrderNoteRequest')

export type OrderChannel = z.infer<typeof OrderChannelSchema>
export type Buyer = z.infer<typeof BuyerSchema>
export type OrderItem = z.infer<typeof OrderItemSchema>
export type ShippingMethod = z.infer<typeof ShippingMethodSchema>
export type PaymentStatusWire = z.infer<typeof PaymentStatusSchema>
export type ShippingAddress = z.infer<typeof ShippingAddressSchema>
export type OrderFulfilment = z.infer<typeof OrderFulfilmentSchema>
export type Order = z.infer<typeof OrderSchema>
export type OrderMessage = z.infer<typeof OrderMessageSchema>
export type OrderMessageFrom = z.infer<typeof OrderMessageFromSchema>
export type ListOrdersQuery = z.infer<typeof ListOrdersQuerySchema>
export type OpenOrdersCountResponse = z.infer<typeof OpenOrdersCountResponseSchema>
export type AddOrderMessageRequest = z.infer<typeof AddOrderMessageRequestSchema>
export type UpdateOrderStatusRequest = z.infer<typeof UpdateOrderStatusRequestSchema>
export type SetOrderNoteRequest = z.infer<typeof SetOrderNoteRequestSchema>
export type RefundOrderRequest = z.infer<typeof RefundOrderRequestSchema>
export type OrderRefundLineState = z.infer<typeof OrderRefundLineStateSchema>
export type OrderRefundState = z.infer<typeof OrderRefundStateSchema>
export type PendingRefund = z.infer<typeof PendingRefundSchema>
